import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";

import { cn } from "../lib/utils";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

const DEFAULT_TICK_COUNT = 8;

const getDefaultActivePointIndex = (length) =>
  length > 4 ? 4 : Math.max(length - 1, 0);

const getAxisBounds = (values) => {
  if (values.length === 0) {
    return {
      max: 1,
      min: 0,
    };
  }

  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  if (minValue === maxValue) {
    const offset = Math.max(Math.abs(minValue) * 0.1, 1);

    return {
      max: maxValue + offset,
      min: minValue - offset,
    };
  }

  const padding = Math.max((maxValue - minValue) * 0.12, 1);

  return {
    max: maxValue + padding,
    min: minValue - padding,
  };
};

const formatAxisLabel = (value) => {
  const normalizedValue = Math.abs(value) >= 1000 ? value / 1000 : value;
  const formattedValue = normalizedValue.toFixed(1).replace(/\.0$/, "");

  return `${value < 0 ? "-" : ""}${formattedValue.replace(/^-/, "")}`;
};

function ChartState({ title, body, actionLabel, onAction }) {
  return (
    <div className="flex h-[222px] items-center justify-center rounded-2xl border border-dashed border-[#EBE9EF] bg-[#FAF9FC] px-6 text-center">
      <div>
        <p className="text-base font-semibold leading-6 text-[#423E51]">
          {title}
        </p>
        <p className="mt-2 text-sm leading-5 text-[#6D6584]">{body}</p>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className="mt-5 inline-flex h-10 items-center justify-center rounded-full border border-[#F1EDF7] px-5 text-sm font-semibold leading-5 text-[#6D6584] transition-colors hover:bg-[#FFFFFF]"
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function Zar8YieldPositionChart({
  series = [],
  isLoading = false,
  hasError = false,
  onRetry,
}) {
  const chartAreaRef = useRef(null);
  const [activePointIndex, setActivePointIndex] = useState(() =>
    getDefaultActivePointIndex(series.length)
  );
  const labels = useMemo(
    () => series.map((point) => point.shortLabel),
    [series]
  );
  const values = useMemo(() => series.map((point) => point.value), [series]);
  const activePoint = series.at(activePointIndex) || null;
  const axisBounds = useMemo(() => getAxisBounds(values), [values]);
  const axisTickLabels = useMemo(() => {
    const tickStep =
      (axisBounds.max - axisBounds.min) / Math.max(DEFAULT_TICK_COUNT - 1, 1);

    return Array.from({ length: DEFAULT_TICK_COUNT }, (_, index) =>
      formatAxisLabel(axisBounds.max - tickStep * index)
    );
  }, [axisBounds.max, axisBounds.min]);

  useEffect(() => {
    setActivePointIndex(getDefaultActivePointIndex(series.length));
  }, [series.length]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          data: values,
          fill: false,
          borderColor: "#EB3366",
          tension: 0.35,
          borderWidth: 2,
          pointRadius: (context) =>
            context.dataIndex === activePointIndex ? 4 : 0,
          pointHoverRadius: (context) =>
            context.dataIndex === activePointIndex ? 5 : 0,
          pointBackgroundColor: "#EB3366",
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointHitRadius: 14,
        },
      ],
    }),
    [activePointIndex, labels, values]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      layout: {
        padding: 0,
      },
      scales: {
        x: {
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          min: axisBounds.min,
          max: axisBounds.max,
          display: false,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      interaction: {
        intersect: false,
        mode: "index",
      },
    }),
    [axisBounds.max, axisBounds.min]
  );

  const handleChartMouseMove = (event) => {
    if (!chartAreaRef.current || series.length === 0) {
      return;
    }

    const bounds = chartAreaRef.current.getBoundingClientRect();
    const relativeX = Math.min(
      Math.max(event.clientX - bounds.left, 0),
      bounds.width
    );
    const nextIndex = Math.round(
      (relativeX / Math.max(bounds.width, 1)) * Math.max(series.length - 1, 0)
    );

    setActivePointIndex(nextIndex);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-[222px] rounded-2xl bg-[#F8F6FB]" />
      </div>
    );
  }

  if (hasError) {
    return (
      <ChartState
        title="Performance unavailable"
        body="We couldn’t load this position’s chart right now."
        actionLabel="Retry"
        onAction={onRetry}
      />
    );
  }

  if (series.length === 0) {
    return (
      <ChartState
        title="No performance history yet"
        body="Performance data will appear here once this position starts generating chart history."
      />
    );
  }

  return (
    <div className="relative h-[222px] w-full">
      <div className="absolute left-0 top-[3px] flex h-[182px] w-[25px] flex-col justify-between text-right text-[12px] leading-[14px] text-[#9D96AF]">
        {axisTickLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>

      <div
        ref={chartAreaRef}
        className="absolute bottom-[22px] left-[34px] right-0 top-[3px]"
        onMouseMove={handleChartMouseMove}
        onMouseLeave={() =>
          setActivePointIndex(getDefaultActivePointIndex(series.length))
        }
      >
        <div className="absolute inset-0 grid grid-rows-8">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="border-t border-[#F1EDF7] opacity-20 first:border-t-0"
            />
          ))}
        </div>

        <Line data={data} options={options} />

        {activePoint ? (
          <>
            <div
              className="absolute bottom-[18px] top-0 border-l border-dashed border-[#EB3366]"
              style={{
                left: `${(activePointIndex / Math.max(series.length - 1, 1)) * 100}%`,
                transform: "translateX(-0.5px)",
              }}
            />
            <div
              className="absolute rounded-[7px] bg-[#423E51] px-2 py-1 text-[10px] font-semibold leading-[12px] text-white"
              style={{
                left: `${(activePointIndex / Math.max(series.length - 1, 1)) * 100}%`,
                top: "-2px",
                transform: "translate(-50%, -100%)",
              }}
            >
              <span>{activePoint.fullLabel}</span>
              <span className="ml-1">
                {formatFiatWithTwoDecimals(activePoint.value)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      <div className="absolute bottom-0 left-[34px] right-0 flex items-start justify-between text-center text-[12px] leading-[14px] text-[#9D96AF]">
        {labels.map((label, index) => (
          <span
            key={`${label}-${index}`}
            className={cn(
              "min-w-[24px]",
              index === activePointIndex && "font-bold text-[#6D6584]"
            )}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
